/*
 * MAXGUARD theme enhancements (progressive; every feature has a server-rendered fallback).
 * - <mg-device-finder>: upgrades the device menu links into brand → model selects.
 * - <mg-sticky-atc>: compact add-to-cart bar that mirrors the real product form button.
 * - Variant-scoped blocks ([data-mg-variant-scope]) are refreshed from Dawn's section re-render.
 * Loaded with `defer`, after constants.js / pubsub.js / global.js.
 */
(() => {
  if (window.mgThemeLoaded) return;
  window.mgThemeLoaded = true;

  const strings = window.mgStrings || {};

  /* ---------- Device finder ---------- */
  if (!customElements.get('mg-device-finder')) {
    class MgDeviceFinder extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready) return;
        this.dataset.ready = 'true';

        this.controls = this.querySelector('.mg-device-finder__controls');
        this.brandSelect = this.querySelector('[data-brand-select]');
        this.modelSelect = this.querySelector('[data-model-select]');
        this.goButton = this.querySelector('[data-go]');
        this.status = this.querySelector('[data-status]');
        this.linksNav = this.querySelector('[data-device-links]');

        this.brands = Array.from(this.querySelectorAll('[data-brand]')).map((brandEl) => ({
          name: brandEl.dataset.brand,
          links: Array.from(brandEl.querySelectorAll('a[data-device-link]')).map((a) => ({
            title: a.textContent.trim(),
            url: a.getAttribute('href'),
            group: a.dataset.group || '',
          })),
        }));
        if (!this.brands.length || !this.controls) return;

        this.brands.forEach((brand, index) => {
          const option = document.createElement('option');
          option.value = String(index);
          option.textContent = brand.name;
          this.brandSelect.append(option);
        });

        this.brandSelect.addEventListener('change', () => this.onBrandChange());
        this.modelSelect.addEventListener('change', () => this.onModelChange());
        this.goButton.addEventListener('click', () => this.go());

        this.controls.hidden = false;
        this.linksNav.hidden = true;
      }

      onBrandChange() {
        const brand = this.brands[Number(this.brandSelect.value)];
        const placeholder = this.modelSelect.options[0];
        this.modelSelect.replaceChildren(placeholder);
        this.goButton.disabled = true;

        if (!brand) {
          this.modelSelect.disabled = true;
          return;
        }

        const groups = new Map();
        brand.links.forEach((link) => {
          if (!groups.has(link.group)) groups.set(link.group, []);
          groups.get(link.group).push(link);
        });
        groups.forEach((links, group) => {
          const parent = group ? document.createElement('optgroup') : this.modelSelect;
          if (group) parent.label = group;
          links.forEach((link) => {
            const option = document.createElement('option');
            option.value = link.url;
            option.textContent = link.title;
            parent.append(option);
          });
          if (group) this.modelSelect.append(parent);
        });

        this.modelSelect.disabled = false;
        if (this.status) {
          this.status.textContent = `${brand.name}: ${brand.links.length}`;
        }
      }

      onModelChange() {
        this.goButton.disabled = !this.modelSelect.value;
      }

      go() {
        const url = this.modelSelect.value;
        // Only follow same-origin relative paths or http(s) URLs that came from the menu markup.
        if (url && (url.startsWith('/') || /^https?:\/\//i.test(url))) window.location.assign(url);
      }
    }
    customElements.define('mg-device-finder', MgDeviceFinder);
  }

  /* ---------- Sticky add to cart ---------- */
  if (!customElements.get('mg-sticky-atc')) {
    class MgStickyAtc extends HTMLElement {
      connectedCallback() {
        const sectionId = this.dataset.section;
        this.mainButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
        this.button = this.querySelector('.mg-sticky-atc__button');
        this.label = this.querySelector('.mg-sticky-atc__label');
        if (!this.mainButton || !this.button) return;

        this.syncState();
        this.mutationObserver = new MutationObserver(() => this.syncState());
        this.mutationObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        // Passive, rAF-throttled scroll check: robust even when a fast fling or anchor jump skips past the
        // button without an IntersectionObserver crossing.
        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.ticking = false;
            this.toggle(this.mainButton.isConnected && this.mainButton.getBoundingClientRect().bottom < 0);
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.onScroll();
      }

      disconnectedCallback() {
        this.mutationObserver?.disconnect();
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.toggle(false);
      }

      syncState() {
        const disabled = this.mainButton.hasAttribute('disabled');
        this.button.toggleAttribute('disabled', disabled);
        this.button.classList.toggle('loading', this.mainButton.classList.contains('loading'));
        this.button.setAttribute('aria-disabled', this.mainButton.getAttribute('aria-disabled') || 'false');
        const mainLabel = this.mainButton.querySelector('span');
        if (mainLabel && this.label) this.label.textContent = mainLabel.textContent.trim();
      }

      toggle(show) {
        if (this.hidden === !show) return;
        this.hidden = !show;
        // Keep focused content clear of the bar (WCAG 2.4.11) and leave room at the end of the page.
        const height = show ? `${this.offsetHeight}px` : '';
        document.documentElement.style.setProperty('--mg-sticky-atc-height', height || '0px');
        document.documentElement.classList.toggle('mg-has-sticky-atc', show);
      }
    }
    customElements.define('mg-sticky-atc', MgStickyAtc);
  }

  /* ---------- Variant-scoped blocks ---------- */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const { sectionId, html } = event?.data || {};
      if (!html) return;
      document.querySelectorAll('[data-mg-variant-scope]').forEach((destination) => {
        if (!destination.id || !destination.id.endsWith(`-${sectionId}`)) return;
        const source = html.getElementById(destination.id);
        if (source) destination.innerHTML = source.innerHTML;
      });
      const stickyPrice = document.getElementById(`MgStickyPrice-${sectionId}`);
      const stickyPriceSource = html.getElementById(`MgStickyPrice-${sectionId}`);
      if (stickyPrice && stickyPriceSource) stickyPrice.innerHTML = stickyPriceSource.innerHTML;
    });
  }

  /* ---------- Expose strings for other modules ---------- */
  window.mgTheme = { strings };
})();

// MAXGUARD: background videos (hero spotlight, feature split) stay still for people who prefer reduced motion.
const mgReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
if (mgReducedMotion) {
  document.querySelectorAll('.mg-hero__video, .mg-split__video').forEach((v) => {
    v.removeAttribute('autoplay');
    v.pause();
  });
}

// Desktop menus open on hover (owner request; reference nomadgoods.com/ae). Dawn's details element still handles click,
// keyboard and focus; hover only opens it, and a short delay lets the pointer cross into the floating card.
const mgHoverMenus = window.matchMedia('(hover: hover) and (min-width: 990px)');
document.querySelectorAll('.header__inline-menu header-menu').forEach((menu) => {
  const details = menu.querySelector('details');
  const summary = details?.querySelector('summary');
  if (!details || !summary) return;
  let timer;
  menu.addEventListener('pointerenter', () => {
    if (!mgHoverMenus.matches) return;
    clearTimeout(timer);
    document.querySelectorAll('.header__inline-menu header-menu').forEach((other) => other !== menu && other.close?.());
    if (!details.open) {
      details.open = true;
      summary.setAttribute('aria-expanded', 'true');
    }
    details.dataset.mgHover = 'true';
  });
  menu.addEventListener('pointerleave', () => {
    if (!mgHoverMenus.matches) return;
    timer = setTimeout(() => {
      delete details.dataset.mgHover;
      menu.close?.();
    }, 160);
  });
  // A click while the pointer already opened the menu keeps it open instead of toggling it shut.
  summary.addEventListener('click', (event) => {
    if (details.dataset.mgHover && details.open) event.preventDefault();
  });
});

// A hero with a mobile still plays its video on wide screens only (phones show the still and never load the video).
const mgWideScreen = window.matchMedia('(min-width: 750px)');
function mgDesktopHeroVideos() {
  document.querySelectorAll('.mg-hero__backdrop--desktop-video .mg-hero__video').forEach((v) => {
    if (mgWideScreen.matches && !mgReducedMotion) v.play().catch(() => {});
    else v.pause();
  });
}
mgDesktopHeroVideos();
mgWideScreen.addEventListener('change', mgDesktopHeroVideos);

// Section videos (mg-section-video) play only while a quarter of them is on screen. A desktop-only video is hidden on
// phones, so it never intersects there and never plays.
if (!mgReducedMotion && 'IntersectionObserver' in window) {
  const mgVideoObserver = new IntersectionObserver(
    (entries) => entries.forEach(({ target, isIntersecting }) => (isIntersecting ? target.play().catch(() => {}) : target.pause())),
    { threshold: 0.25 }
  );
  const mgObserveVideos = (root) => root.querySelectorAll('.mg-inview-video').forEach((v) => mgVideoObserver.observe(v));
  mgObserveVideos(document);
  document.addEventListener('shopify:section:load', (event) => mgObserveVideos(event.target));
}


// Product gallery: on wide screens the thumbnails form a vertical rail beside the photo (mg-product.css). Dawn's
// slider only scrolls sideways, so the rail's up/down steps, their state, and keeping the selected thumbnail in view
// are handled here.
function mgInitGalleryRail(root) {
  root.querySelectorAll('media-gallery').forEach((gallery) => {
    const list = gallery.querySelector('.thumbnail-list');
    const up = gallery.querySelector('.mg-rail-btn--up');
    const down = gallery.querySelector('.mg-rail-btn--down');
    if (!list || !up || !down || list.dataset.mgRail) return;
    list.dataset.mgRail = 'true';
    const isRail = () => getComputedStyle(list).flexDirection === 'column';
    const update = () => {
      const scrolls = isRail() && list.scrollHeight > list.clientHeight + 1;
      up.hidden = down.hidden = !scrolls;
      // A few pixels of slack: scroll snapping rests the first thumbnail just inside the list's padding.
      up.disabled = list.scrollTop <= 4;
      down.disabled = list.scrollTop + list.clientHeight >= list.scrollHeight - 4;
    };
    const step = (dir) => list.scrollBy({ top: dir * list.clientHeight * 0.8, behavior: mgReducedMotion ? 'auto' : 'smooth' });
    up.addEventListener('click', () => step(-1));
    down.addEventListener('click', () => step(1));
    list.addEventListener('scroll', update, { passive: true });
    new ResizeObserver(update).observe(list);
    new MutationObserver(() => {
      const current = list.querySelector('[aria-current]')?.closest('li');
      if (!current || !isRail()) return;
      const box = list.getBoundingClientRect();
      const item = current.getBoundingClientRect();
      if (item.top < box.top) list.scrollBy({ top: item.top - box.top });
      else if (item.bottom > box.bottom) list.scrollBy({ top: item.bottom - box.bottom });
    }).observe(list, { subtree: true, attributeFilter: ['aria-current'] });
    update();
  });
}
mgInitGalleryRail(document);
document.addEventListener('shopify:section:load', (event) => mgInitGalleryRail(event.target));

// "Buy now" (buy-buttons.liquid): straight to checkout with only this product and quantity, through a cart permalink,
// so whatever is already in the cart stays there. It mirrors Add to cart's disabled state as the variant changes.
document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-mg-buy-now]');
  if (!link) return;
  event.preventDefault();
  if (link.getAttribute('aria-disabled') === 'true') return;
  const formId = link.dataset.mgBuyNow;
  const variantId = document.querySelector(`#${CSS.escape(formId)} [name="id"]`)?.value;
  const quantityInput = document.querySelector(`[name="quantity"][form="${CSS.escape(formId)}"]`);
  const quantity = Math.max(1, parseInt(quantityInput?.value, 10) || 1);
  if (variantId) window.location.assign(`${window.Shopify?.routes?.root || '/'}cart/${variantId}:${quantity}`);
});
document.querySelectorAll('[data-mg-buy-now]').forEach((link) => {
  const addButton = link.closest('.product-form__buttons')?.querySelector('.product-form__submit');
  if (!addButton) return;
  const sync = () => {
    if (addButton.hasAttribute('disabled')) {
      link.setAttribute('aria-disabled', 'true');
      link.setAttribute('tabindex', '-1');
    } else {
      link.removeAttribute('aria-disabled');
      link.removeAttribute('tabindex');
    }
  };
  new MutationObserver(sync).observe(addButton, { attributeFilter: ['disabled'] });
});

// Collection category rows scroll sideways on phones: bring the current family and sub-category into view.
(function () {
  const reveal = (root) =>
    root.querySelectorAll('.mg-cats__list').forEach((list) => {
      const current = list.querySelector('[aria-current]');
      if (current && list.scrollWidth > list.clientWidth) list.scrollLeft = current.parentElement.offsetLeft - list.offsetLeft - 16;
    });
  reveal(document);
  window.addEventListener('load', () => reveal(document), { once: true });
  document.addEventListener('shopify:section:load', (e) => reveal(e.target));
})();
